import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * useEntregasSelection — Estado de seleção múltipla + ações em lote da tabela.
 * Extraído de Entregas.jsx (fev/2026 code-quality) para isolar a lógica
 * de bulk assign/delete das rotinas de CRUD unitário.
 *
 * `filtered` é a lista atualmente VISÍVEL (após filtros) — usada por toggleSelectAll
 * e deleteAllVisible para operar apenas no escopo que o usuário vê.
 * `onDone` é chamado após cada ação bem-sucedida (tipicamente `load()` do data hook).
 */
export function useEntregasSelection(filtered, onDone, { onRotaAssigned } = {}) {
  const [selected, setSelected] = useState(new Set());
  const [bulkEntregador, setBulkEntregador] = useState("");
  const [bulkData, setBulkData] = useState("");
  const [bulkRota, setBulkRota] = useState("");

  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleSelectAll = () =>
    setSelected((s) => (s.size === filtered.length ? new Set() : new Set(filtered.map((x) => x.id))));
  const clearSelection = () => setSelected(new Set());

  const bulkAssignEntregador = async () => {
    if (!bulkEntregador) return toast.error("Selecione um entregador");
    if (selected.size === 0) return toast.error("Nenhuma entrega selecionada");
    try {
      await api.post("/entregas/bulk-assign", { ids: Array.from(selected), entregador_id: bulkEntregador });
      toast.success(`${selected.size} entrega(s) atribuídas`);
      clearSelection(); setBulkEntregador(""); onDone?.();
    } catch (e) {
      console.error("Falha no bulk-assign", e);
      toast.error(e?.response?.data?.detail || "Erro ao atribuir em lote");
    }
  };

  const bulkUpdateData = async () => {
    if (!bulkData) return toast.error("Escolha uma data");
    if (selected.size === 0) return toast.error("Nenhuma entrega selecionada");
    try {
      const r = await api.post("/entregas/bulk-update-data", { ids: Array.from(selected), data: bulkData });
      toast.success(`Data de ${r.data.updated || selected.size} entrega(s) atualizada para ${bulkData.split("-").reverse().join("/")}`);
      clearSelection(); setBulkData(""); onDone?.();
    } catch (e) {
      console.error("Falha no bulk-update-data", e);
      toast.error(e?.response?.data?.detail || "Erro ao alterar data em lote");
    }
  };

  // Iter 99 (Bloco 3): define/remove a rota de N entregas. "__none__" = sem rota.
  const bulkAssignRota = async () => {
    if (!bulkRota) return toast.error("Selecione uma rota");
    if (selected.size === 0) return toast.error("Nenhuma entrega selecionada");
    try {
      const r = await api.post("/entregas/bulk-assign-rota", { ids: Array.from(selected), rota_id: bulkRota === "__none__" ? null : bulkRota });
      const { modified, rota, bairros_vinculados: vinc = 0, rotas_afetadas: afet = 0 } = r.data;
      if (rota) {
        const extra = vinc > 0 ? ` · ${vinc} bairro(s) vinculado(s) à rota${afet > 0 ? ` (removido(s) de ${afet} outra(s) rota(s))` : ""}` : "";
        toast.success(`${modified} entrega(s) movida(s) para a rota "${rota.nome}"${extra}`);
        if (vinc > 0 || afet > 0) onRotaAssigned?.();
      } else {
        toast.success(`${modified} entrega(s) ficaram sem rota`);
      }
      clearSelection(); setBulkRota(""); onDone?.();
    } catch (e) {
      console.error("Falha no bulk-assign-rota", e);
      toast.error(e?.response?.data?.detail || "Erro ao atribuir rota em lote");
    }
  };

  const bulkDeleteSelected = async () => {
    if (selected.size === 0) return toast.error("Nenhuma entrega selecionada");
    try {
      await api.post("/entregas/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${selected.size} entrega(s) excluídas`);
      clearSelection(); onDone?.();
    } catch (e) {
      console.error("Falha no bulk-delete", e);
      toast.error(e?.response?.data?.detail || "Erro ao excluir em lote");
    }
  };

  const deleteAllVisible = async () => {
    if (filtered.length === 0) return;
    try {
      await api.post("/entregas/bulk-delete", { ids: filtered.map((x) => x.id) });
      toast.success(`${filtered.length} entrega(s) excluídas`);
      clearSelection(); onDone?.();
    } catch (e) {
      console.error("Falha no delete-all-visible", e);
      toast.error(e?.response?.data?.detail || "Erro ao excluir visíveis");
    }
  };

  return {
    selected, bulkEntregador, setBulkEntregador,
    bulkData, setBulkData, bulkRota, setBulkRota,
    toggleSelect, toggleSelectAll, clearSelection,
    bulkAssignEntregador, bulkUpdateData, bulkAssignRota, bulkDeleteSelected, deleteAllVisible,
  };
}
